/**
 * Which decorative frame (if any) the Image + Text block draws behind its image.
 * Pure, so the rule is testable and the renderer only maps a name to markup.
 *
 * Editors pick from the block's `imageFrame` select. `none` (the default —
 * and what every block created before the field existed resolves to) draws
 * nothing. `auto` keeps the legacy behaviour: rotate the four styles by the
 * block's position on the page.
 */
export type ImageFrame = 'backdrop' | 'dots' | 'border' | 'bar';

/** Legacy rotation order — index 0..3 mapped to these before the field existed. */
export const IMAGE_FRAME_ROTATION: readonly ImageFrame[] = ['backdrop', 'dots', 'border', 'bar'];

/** Values an editor can pick that map to a drawn frame (the rotation set). Named to avoid confusion with the renderer's FRAMES markup map. */
const KNOWN_FRAMES: ReadonlySet<string> = new Set(IMAGE_FRAME_ROTATION);
const isImageFrame = (v: string): v is ImageFrame => KNOWN_FRAMES.has(v);

export function resolveImageFrame(value: unknown, index: number | undefined): ImageFrame | null {
  if (typeof value !== 'string') return null;
  if (value === 'auto') {
    const i = typeof index === 'number' && Number.isInteger(index) && index >= 0 ? index : 0;
    return IMAGE_FRAME_ROTATION[i % IMAGE_FRAME_ROTATION.length];
  }
  return isImageFrame(value) ? value : null;
}
